'use client';

import { useEffect, useState } from 'react';

interface CountDownTimerProps {
  remainingSeconds: number;
  flashSaleData: {
    time_word?: string;
  };
  className?: string;
  isVisible?: boolean; // 控制倒计时数字显示/隐藏
  onCountdownEnd?: () => void; // 倒计时结束回调函数
}

export default function CountDownTimer({
  remainingSeconds,
  flashSaleData,
  className = '',
  isVisible = true,
  onCountdownEnd,
}: CountDownTimerProps) {
  const [hour, setHour] = useState('00');
  const [min, setMin] = useState('00');
  const [sec, setSec] = useState('00');

  // 倒计时逻辑
  const startCountDown = (val: number) => {
    if (val <= 0) {
      setHour('00');
      setMin('00');
      setSec('00');
      // 倒计时结束时调用回调函数
      // 注释：不再需要清理sessionStorage，因为页面返回时会重新调用接口
      // if (typeof window !== 'undefined') {
      //   sessionStorage.removeItem('flash-sale-end-time');
      //   sessionStorage.removeItem('flash-sale-initial-remaining');
      // }
      if (onCountdownEnd) {
        onCountdownEnd();
      }
      return;
    }

    const hours = Math.floor(val / 3600);
    const minutes = Math.floor((val % 3600) / 60);
    const seconds = val % 60;

    setHour(hours > 9 ? String(hours) : `0${hours}`);
    setMin(minutes > 9 ? String(minutes) : `0${minutes}`);
    setSec(seconds > 9 ? String(seconds) : `0${seconds}`);
  };

  // 直接使用 remainingSeconds 计算结束时间戳
  // 注释：不再使用sessionStorage持久化，因为页面返回时会重新调用接口获取最新数据
  const [endTime, setEndTime] = useState(() => {
    return Date.now() + remainingSeconds * 1000;
  });

  // 当 remainingSeconds 变化时，重新计算结束时间戳
  // 注释：不再检查sessionStorage，直接使用新的 remainingSeconds
  useEffect(() => {
    const newEndTime = Date.now() + remainingSeconds * 1000;
    setEndTime(newEndTime);
  }, [remainingSeconds]);

  // 以下是原来使用 sessionStorage 的代码，已注释
  // const [endTime, setEndTime] = useState(() => {
  //   if (typeof window === 'undefined') return Date.now() + remainingSeconds * 1000;
  //
  //   const storageKey = 'flash-sale-end-time';
  //   const initialRemainingKey = 'flash-sale-initial-remaining';
  //   const storedEndTime = sessionStorage.getItem(storageKey);
  //   const storedInitialRemaining = sessionStorage.getItem(initialRemainingKey);
  //
  //   // 检查是否是同一个倒计时轮次（基于初始剩余秒数）
  //   if (storedEndTime && storedInitialRemaining) {
  //     const endTimeValue = parseInt(storedEndTime);
  //     const initialRemaining = parseInt(storedInitialRemaining);
  //
  //     // 如果初始剩余秒数相同且结束时间未过期，使用存储的时间
  //     if (initialRemaining === remainingSeconds && endTimeValue > Date.now()) {
  //       return endTimeValue;
  //     }
  //   }
  //
  //   // 清除旧的缓存数据，计算新的结束时间
  //   sessionStorage.removeItem(storageKey);
  //   sessionStorage.removeItem(initialRemainingKey);
  //
  //   const newEndTime = Date.now() + remainingSeconds * 1000;
  //   sessionStorage.setItem(storageKey, newEndTime.toString());
  //   sessionStorage.setItem(initialRemainingKey, remainingSeconds.toString());
  //   return newEndTime;
  // });
  //
  // // 当remainingSeconds变化时，检查是否需要重新计算结束时间戳
  // useEffect(() => {
  //   if (typeof window === 'undefined') return;
  //
  //   const storageKey = 'flash-sale-end-time';
  //   const initialRemainingKey = 'flash-sale-initial-remaining';
  //   const storedEndTime = sessionStorage.getItem(storageKey);
  //   const storedInitialRemaining = sessionStorage.getItem(initialRemainingKey);
  //
  //   // 如果是新的倒计时轮次或时间已过期，重新计算
  //   if (
  //     !storedEndTime ||
  //     !storedInitialRemaining ||
  //     parseInt(storedInitialRemaining) !== remainingSeconds ||
  //     parseInt(storedEndTime) <= Date.now()
  //   ) {
  //     // 清除旧缓存
  //     sessionStorage.removeItem(storageKey);
  //     sessionStorage.removeItem(initialRemainingKey);
  //
  //     // 重新计算并存储
  //     const newEndTime = Date.now() + remainingSeconds * 1000;
  //     sessionStorage.setItem(storageKey, newEndTime.toString());
  //     sessionStorage.setItem(initialRemainingKey, remainingSeconds.toString());
  //     setEndTime(newEndTime);
  //   }
  // }, [remainingSeconds]);

  // 基于结束时间戳的实时倒计时
  useEffect(() => {
    // 计算当前剩余秒数的函数
    const getCurrentSeconds = () => {
      const now = Date.now();
      const remaining = Math.floor((endTime - now) / 1000);
      return Math.max(0, remaining);
    };

    // 立即更新一次
    startCountDown(getCurrentSeconds());

    const timer = setInterval(() => {
      const currentSeconds = getCurrentSeconds();
      startCountDown(currentSeconds);
    }, 1000);

    return () => {
      clearInterval(timer);
    };
  }, [endTime]);

  return (
    <div className={`flex items-center text-[#282828] ${className}`}>
      {/* 倒计时标题 */}
      {flashSaleData.time_word && (
        <div className="text-[12px] lg:text-[15px] xl:text-[16px] mr-[5px] lg:mr-[10px]">
          {flashSaleData.time_word}
        </div>
      )}

      {/* 倒计时容器 */}
      <div className="flex items-center gap-[6px] lg:gap-[8px]">
        {/* 小时 */}
        <div className="flex items-center justify-center bg-[#282828] rounded-[4px] lg:rounded-[8px] min-w-[20px] lg:min-w-[36px] xl:min-w-[40px] h-[20px] lg:h-[36px] xl:h-[40px] text-center">
          {isVisible && (
            <span className="text-[11px] lg:text-[20px] xl:text-[22px] font-bb text-white">
              {hour}
            </span>
          )}
        </div>

        {/* 分隔符 */}
        <span className="text-[11px] lg:text-[28px] xl:text-[32px] font-bb text-[#282828]">:</span>

        {/* 分钟 */}
        <div className="flex items-center justify-center bg-[#282828] rounded-[4px] lg:rounded-[8px] min-w-[20px] lg:min-w-[36px] xl:min-w-[40px] h-[20px] lg:h-[36px] xl:h-[40px] text-center">
          {isVisible && (
            <span className="text-[11px] lg:text-[20px] xl:text-[22px] font-bb text-white">
              {min}
            </span>
          )}
        </div>

        {/* 分隔符 */}
        <span className="text-[11px] lg:text-[28px] xl:text-[32px] font-bb text-[#282828]">:</span>

        {/* 秒 */}
        <div className="flex items-center justify-center bg-[#282828] rounded-[4px] lg:rounded-[8px] min-w-[20px] lg:min-w-[36px] xl:min-w-[40px] h-[20px] lg:h-[36px] xl:h-[40px] text-center">
          {isVisible && (
            <span className="text-[11px] lg:text-[20px] xl:text-[22px] font-bb text-white">
              {sec}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
